import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, IconButton, TableList, Tooltip, Typography, WithInfoIcon } from 'seagull';
import { Trash } from 'seagull/icons';
import TimeActivityTooltip from '~/components/TimeActivityTooltip/TimeActivityTooltip';
import { emptyCell } from '~/constants';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { QboIntegrationLineItemType } from '~/models/integration/model';
import { LineItemType } from '~/models/invoice-draft';
import { LineItemPendingApprovalReason } from '~/types/api.model';
import { utils } from '~/utils';
import styles from '../ScheduledInvoice.module.scss';
import { makeTestId, displayLineItemRate, getClientStatusTooltip, getVendorStatusTooltip, pendingApprovalReasonsClientTextsPath, pendingApprovalReasonsVendorTextsPath } from '../ScheduledInvoice.utils';
import { SubLineItemRow } from './SubLineItem';
const lineItemTextsPath = 'pages.agreementMinisite.scheduledInvoice.lineItem';
export function CustomerLineItem(props) {
    const { onApprove } = props;
    const { pendingApprovalReason } = props.charge;
    const { t: clientTexts } = useLocaleKeys(pendingApprovalReasonsClientTextsPath);
    const { t: lineItemTexts } = useLocaleKeys(lineItemTextsPath);
    const testId = makeTestId(props.charge.id);
    const action = pendingApprovalReason === LineItemPendingApprovalReason.AdHocCharge ? /*#__PURE__*/ _jsx(Button, {
        bi: {
            name: 'approve_ad_hoc_charge'
        },
        skin: "primaryOutline",
        intent: Intent.ApproveCharge,
        size: "small",
        testId: "".concat(testId, "-approve"),
        onClick: onApprove,
        children: lineItemTexts.approveButton()
    }) : null;
    return /*#__PURE__*/ _jsx(LineItem, _object_spread_props(_object_spread({}, props), {
        testId: testId,
        action: action,
        getStatusTooltip: (reason)=>getClientStatusTooltip(reason, clientTexts)
    }));
}
export function VendorLineItem(props) {
    const { onRemove } = props;
    const { t: vendorTexts } = useLocaleKeys(pendingApprovalReasonsVendorTextsPath);
    const { t: lineItemTexts } = useLocaleKeys(lineItemTextsPath);
    const testId = makeTestId(props.charge.id);
    const action = /*#__PURE__*/ _jsx(VendorLineItemAction, _object_spread({
        testId: testId
    }, makeRemoveButtonProps(props.charge, onRemove, lineItemTexts)));
    return /*#__PURE__*/ _jsx(LineItem, _object_spread_props(_object_spread({}, props), {
        testId: testId,
        action: action,
        getStatusTooltip: (reason)=>getVendorStatusTooltip(reason, vendorTexts)
    }));
}
const makeRemoveButtonProps = (charge, onRemove, lineItemTexts)=>{
    const { type } = charge;
    let removeButton;
    switch(type){
        case LineItemType.Credit:
            removeButton = {
                disabled: true,
                tooltip: lineItemTexts.removeButton.creditDisabled(),
                dataIntent: 'credit',
                intent: undefined
            };
            break;
        case LineItemType.Automatic:
            removeButton = {
                disabled: true,
                tooltip: lineItemTexts.removeButton.automaticDisabled(),
                dataIntent: 'ongoing-service',
                intent: undefined
            };
            break;
        case LineItemType.Manual:
        case LineItemType.AdHocCharge:
            removeButton = {
                disabled: false,
                tooltip: lineItemTexts.removeButton.remove(),
                dataIntent: 'active',
                intent: Intent.RemoveCharge
            };
            break;
    }
    return _object_spread_props(_object_spread({}, removeButton), {
        onClick: onRemove
    });
};
function LineItem(rowProps) {
    var _charge_integrations;
    const { charge, testId, addedLineItemId, action, getStatusTooltip } = rowProps;
    const { SERVICE_TEMPLATE_BUNDLE } = useFeatureFlags();
    const { t: lineItemTexts } = useLocaleKeys(lineItemTextsPath);
    const subLineItems = charge.subLineItems;
    const hasLineItems = SERVICE_TEMPLATE_BUNDLE && subLineItems && subLineItems.length > 0;
    const chargeQboIntegration = (_charge_integrations = charge.integrations) === null || _charge_integrations === void 0 ? void 0 : _charge_integrations.find((integration)=>integration.type === QboIntegrationLineItemType.QBO);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TableList.Row, {
                testId: testId,
                addPulse: charge.id === addedLineItemId,
                size: "small",
                disableMarginBottom: !!charge.note || hasLineItems,
                items: [
                    {
                        value: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            ellipsis: true,
                            testId: "".concat(testId, "-name"),
                            children: charge.name
                        })
                    },
                    {
                        value: charge.quantity ? /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            testId: "".concat(testId, "-quantity"),
                            children: utils.formatNumberWithCommas(charge.quantity)
                        }) : null
                    },
                    {
                        value: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            ellipsis: true,
                            testId: "".concat(testId, "-rate"),
                            children: displayLineItemRate(charge.rate)
                        })
                    },
                    {
                        value: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            testId: "".concat(testId, "-discount"),
                            children: charge.discount ? charge.discount.format() : ''
                        })
                    },
                    {
                        value: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            testId: "".concat(testId, "-total"),
                            children: utils.formatDollarAmount(charge.totalAmount)
                        })
                    },
                    {
                        value: charge.pendingApprovalReason && /*#__PURE__*/ _jsx(WithInfoIcon, {
                            info: getStatusTooltip(charge.pendingApprovalReason),
                            children: /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                testId: "".concat(testId, "-status"),
                                "data-intent": charge.pendingApprovalReason,
                                children: lineItemTexts.requiresApproval()
                            })
                        })
                    },
                    {
                        value: /*#__PURE__*/ _jsxs("div", {
                            className: styles.actions,
                            children: [
                                chargeQboIntegration ? /*#__PURE__*/ _jsx(TimeActivityTooltip, {
                                    timeActivity: chargeQboIntegration.value.timeActivity
                                }) : null,
                                action
                            ]
                        })
                    }
                ]
            }),
            charge.note && /*#__PURE__*/ _jsx(TableList.Row, {
                testId: "".concat(testId, "-note"),
                addPulse: addedLineItemId === charge.id,
                className: styles.gapBottom,
                disableMarginBottom: !!subLineItems && SERVICE_TEMPLATE_BUNDLE,
                size: "fluid",
                items: [
                    {
                        value: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "secondary",
                            breakWords: true,
                            children: charge.note
                        })
                    },
                    emptyCell,
                    emptyCell,
                    emptyCell,
                    emptyCell,
                    emptyCell,
                    emptyCell
                ]
            }),
            subLineItems && SERVICE_TEMPLATE_BUNDLE && subLineItems.map((subLineItem, index)=>/*#__PURE__*/ _jsx(SubLineItemRow, {
                    subLineItem: subLineItem,
                    testId: "".concat(testId, "-sub-line-item-").concat(index),
                    disableMarginBottom: index !== subLineItems.length - 1,
                    quantity: charge.quantity
                }))
        ]
    });
}
function VendorLineItemAction(props) {
    return /*#__PURE__*/ _jsx("div", {
        className: styles.vendorAction,
        children: /*#__PURE__*/ _jsx(IconButton, {
            bi: {
                name: 'remove_line_item'
            },
            noPadding: true,
            size: "small",
            skin: "primaryTransparent",
            disabled: props.disabled,
            testId: "".concat(props.testId, "-remove"),
            "data-intent": props.dataIntent,
            intent: props.intent,
            onClick: props.onClick,
            children: /*#__PURE__*/ _jsx(Tooltip, {
                placement: "top",
                title: props.tooltip,
                children: /*#__PURE__*/ _jsx("span", {
                    children: /*#__PURE__*/ _jsx(Trash, {
                        size: 18
                    })
                })
            })
        })
    });
}
